// domain/services/fundingSourceDating.js
import { toAsOfDate, toDateStr } from './effectiveDating';

/**
 * When a payer's record may begin.
 *
 * ONE RULE, AND IT IS ABOUT THE FIRST VERSION ONLY: a payer starts today or earlier, never later.
 * Later versions are a different question — scheduling a config change for 1 January is the reason the
 * timeline exists at all — and `VersionTimeline.canTakeEffectAt` answers that one.
 *
 * WHY BACKDATING BUT NOT FORWARD-DATING. Backdating is load-bearing: the projector resolves a service
 * date to the config in force then, so a payer whose terms started in January has to be enterable in
 * August. Forward-dating a FIRST version buys nothing, because a payer is only ever reached through a
 * coverage or an authorization and each of those carries its own effective dates — "this client is
 * covered from 1 September" is a fact about the coverage, not about the payer's config. What it does
 * cost is a payer that exists on no date: absent from every picker, with no version in force to amend
 * or retire, and until HR-4574 absent from the settings table that created it.
 *
 * NOT A GENERIC EFFECTIVE-DATING RULE, which is why it is here and not in `effectiveDating`. A
 * client's coverage starting next month is ordinary and must stay possible; this file is the payer's
 * policy, and it says nothing about anything else in the context.
 */

/**
 * The latest date a payer's first version may take, or null when the as-of date cannot be read.
 *
 * The editor's calendar bound and the guard below are both this value, so the option a user is offered
 * and the write that accepts it cannot disagree.
 *
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {string|null} 'YYYY-MM-DD'
 */
export function latestStart(asOf) {
  return toAsOfDate(asOf);
} // latestStart()

/**
 * May a payer's first version take effect on this date?
 *
 * FAILS CLOSED on either date being unreadable, rather than treating "cannot tell" as permission. The
 * comparison is lexicographic, which is chronological for zero-padded calendar dates.
 *
 * @param {string} asOf - 'YYYY-MM-DD'
 * @param {string} effectiveFrom - 'YYYY-MM-DD' the candidate start
 * @returns {boolean}
 */
export function canStartOn(asOf, effectiveFrom) {
  const latest = latestStart(asOf);
  const dateStr = toDateStr(effectiveFrom);

  return latest !== null && dateStr !== null && dateStr <= latest;
} // canStartOn()
